<script lang="ts">
  import { DEFAULT_MIN_CELL_WIDTH } from "@shared/common/data/instagram-gallery";
  import {
    getInstagramGalleryImageStyle,
    getInstagramGalleryItemStyle,
    getInstagramGalleryStyle,
    instagramGalleryCaptionStyle,
    instagramGalleryCaptionWrapperStyle,
    instagramGalleryIconStyle,
    instagramGalleryLinkAreaStyle,
    styleToString,
  } from "@shared/common/node/style";
  import type {
    IInstagramGalleryNode,
    PartialNode,
  } from "@shared/common/node/type";
  import type { InstagramPost } from "@shared/common/types/campaign";
  import { getDataSourceValue } from "@shared/common/utils/data-source";
  import { assertCondition, isObject } from "@shared/common/utils/value";

  import { onMount } from "svelte";
  import { getCurrentDocumentNode, getStore } from "../../context";

  const { dataMap, reactionEventListener: dispatchReactionEvent } = getStore();

  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<IInstagramGalleryNode>;

  const instagramPostsAssertion = assertCondition<InstagramPost[]>(
    (val): val is InstagramPost[] =>
      Array.isArray(val) &&
      val.every(
        (item) =>
          isObject(item) &&
          "permalink" in item &&
          "caption" in item &&
          "previewImageUrl" in item
      )
  );

  let nodeRef: HTMLDivElement | null;

  function calculateNumOfCols() {
    if (!nodeRef) {
      return;
    }

    const computedStyle = getComputedStyle(nodeRef);
    const nodeEleContentWidth =
      nodeRef.getBoundingClientRect().width -
      parseFloat(computedStyle.paddingLeft) -
      parseFloat(computedStyle.paddingRight);

    adjustedNumOfCols = Math.min(
      node?.col ?? 5,
      Math.floor(
        nodeEleContentWidth / (node.minCellWidth ?? DEFAULT_MIN_CELL_WIDTH)
      )
    );
  }

  let adjustedNumOfCols: number = node?.col ?? 5;

  onMount(() => {
    if (!nodeRef || node.minCellWidth === 0) {
      return;
    }

    const observer = new ResizeObserver(calculateNumOfCols);
    observer.observe(nodeRef);

    return () => {
      observer.disconnect();
    };
  });

  $: instagramPostsSliced = instagramPostsAssertion
    .typeSafeValue(
      getDataSourceValue({
        dataMap: $dataMap ?? {},
        keyString: "instagram.posts",
      }),
      []
    )
    .slice(0, adjustedNumOfCols * (node?.row ?? 2));

  function handleLinkClick(e: MouseEvent, index: number, post: InstagramPost) {
    dispatchReactionEvent({
      element: e.target as HTMLElement,
      node,
      documentNode,
      data: {
        nodeId: node.id ?? "",
        nodeName: `${node.name}_${index}_${post.permalink}`,
        trigger: { type: "ON_CLICK" },
        action: { type: "URL", url: post.permalink, target: "_blank" },
      },
      // eslint-disable-next-line no-console
    }).catch((e) => console.error(e));
  }
</script>

<div
  bind:this={nodeRef}
  style={styleToString(
    getInstagramGalleryStyle({ ...node, col: adjustedNumOfCols })
  )}
>
  {#each instagramPostsSliced as post, index}
    <!-- item -->
    <div style={styleToString(getInstagramGalleryItemStyle(node))} class="item">
      <!-- caption-wrapper -->
      <div
        style={styleToString(instagramGalleryCaptionWrapperStyle)}
        class="caption-wrapper"
      >
        <!-- caption -->
        <p style={styleToString(instagramGalleryCaptionStyle)}>
          {post.caption}
        </p>
      </div>

      <!-- icon -->
      {#if post.mediaType === "VIDEO"}
        <svg
          style={styleToString(instagramGalleryIconStyle)}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-video"
          ><path d="m22 8-6 4 6 4V8Z" /><rect
            width="14"
            height="12"
            x="2"
            y="6"
            rx="2"
            ry="2"
          /></svg
        >
      {:else if post.mediaType === "CAROUSEL_ALBUM"}
        <svg
          style={styleToString(instagramGalleryIconStyle)}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-layers"
          ><path
            d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"
          /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path
            d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"
          /></svg
        >
      {/if}

      <!-- image -->
      <img
        style={styleToString(getInstagramGalleryImageStyle(node))}
        src={post.previewImageUrl}
        alt={post.caption}
        draggable={false}
      />

      <!-- link -->
      <a
        on:click={(e) => {
          e.preventDefault();
          handleLinkClick(e, index, post);
        }}
        style={styleToString(instagramGalleryLinkAreaStyle)}
        href={post.permalink}
        target="_blank"
        rel="noopener noreferrer"
      >
        &nbsp;
      </a>
    </div>
  {/each}
</div>

<style>
  .caption-wrapper {
    opacity: 0;
  }
  .item:hover .caption-wrapper {
    opacity: 1;
  }
</style>
